import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import SEO from '../../components/SEO';
import { toast } from 'sonner';
import { 
  Calendar, 
  Clock, 
  ArrowLeft,
  Leaf,
  Factory,
  Truck,
  Zap,
  Calculator,
  CheckCircle,
  FileText,
  Target,
  TrendingDown,
  Award,
  Send,
  Building2,
  Users,
  Recycle,
  BarChart3,
  ArrowRight
} from 'lucide-react';

export default function BilanCarboneMarchesPublicsArticle() {
  const [formData, setFormData] = useState({
    company: '',
    email: '',
    phone: '',
    sector: '',
    employees: '',
    needs: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSubmitted(true);
    setIsSubmitting(false);
    toast.success('Demande envoyée ! Nous vous recontactons sous 24h.');
  };

  return (
    <>
      <SEO 
        title="Bilan Carbone et Marchés Publics : Guide et Accompagnement 2026"
        description="Comment réaliser son bilan carbone pour les marchés publics ? Méthodologie, outils et accompagnement personnalisé pour PME. Devis gratuit."
        path="/blog/bilan-carbone-marches-publics"
      />
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
          <div className="container mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-dce-express.png" alt="DCE Express" className="h-10 w-auto" />
            </Link>
            <div className="flex gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Article */}
        <article className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            {/* Breadcrumb */}
            <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
              <ArrowLeft className="w-4 h-4" />
              Retour au blog
            </Link>

            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                  Environnement
                </span>
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                  Bilan Carbone
                </span>
              </div>
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                Bilan Carbone et Marchés Publics : Obligation, Méthode et Accompagnement
              </h1>
              <div className="flex items-center gap-6 text-slate-500">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  30 mars 2026
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  14 min de lecture
                </span>
              </div>
            </div>

            {/* Featured Image */}
            <div className="relative h-80 rounded-2xl overflow-hidden mb-10">
              <img 
                src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1200" 
                alt="Bilan carbone et environnement"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white text-lg font-medium">
                  Le bilan carbone devient un critère incontournable dans les marchés publics
                </p>
              </div>
            </div>

            {/* CTA Box - Accompagnement */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 mb-10 text-white">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <Calculator className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Besoin d'aide pour votre bilan carbone ?</h3>
                  <p className="text-green-100 mb-4">
                    DCE Express vous accompagne dans la réalisation de votre bilan carbone et la rédaction de votre mémoire RSE pour vos réponses aux appels d'offres.
                  </p>
                  <a href="#formulaire-accompagnement">
                    <Button className="bg-white text-green-700 hover:bg-green-50">
                      Demander un devis gratuit
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Table of Contents */}
            <div className="bg-slate-50 rounded-xl p-6 mb-10">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-violet-600" />
                Sommaire
              </h2>
              <ul className="space-y-2 text-slate-600">
                <li><a href="#introduction" className="hover:text-violet-600">1. Pourquoi le bilan carbone est devenu obligatoire</a></li>
                <li><a href="#methodologie" className="hover:text-violet-600">2. Méthodologie du bilan carbone</a></li>
                <li><a href="#scopes" className="hover:text-violet-600">3. Les 3 scopes d'émissions</a></li>
                <li><a href="#marches-publics" className="hover:text-violet-600">4. Bilan carbone dans les marchés publics</a></li>
                <li><a href="#memoire-rse" className="hover:text-violet-600">5. Rédiger son mémoire RSE/Carbone</a></li>
                <li><a href="#outils" className="hover:text-violet-600">6. Outils et ressources</a></li>
                <li><a href="#formulaire-accompagnement" className="hover:text-violet-600 font-medium text-green-600">7. Demander un accompagnement</a></li>
              </ul>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              {/* Introduction */}
              <section id="introduction" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <Leaf className="w-6 h-6 text-green-600" />
                  1. Pourquoi le Bilan Carbone est Devenu Obligatoire
                </h2>
                <p className="text-slate-600 mb-4">
                  La <strong>transition écologique</strong> est au cœur des politiques publiques françaises. Depuis la loi Climat et Résilience (2021) et le renforcement du Code de la commande publique, le <strong>bilan carbone</strong> est devenu un élément clé pour répondre aux appels d'offres.
                </p>
                
                <div className="grid md:grid-cols-3 gap-4 my-6">
                  <div className="bg-green-50 rounded-xl p-5 text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">2026</div>
                    <p className="text-slate-600 text-sm">Obligation d'un critère environnemental dans tous les marchés</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-5 text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">30%</div>
                    <p className="text-slate-600 text-sm">Poids moyen du critère carbone dans l'attribution</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-5 text-center">
                    <div className="text-3xl font-bold text-amber-600 mb-2">500+</div>
                    <p className="text-slate-600 text-sm">Entreprises de +500 salariés soumises au BEGES</p>
                  </div>
                </div>

                <div className="bg-violet-50 border-l-4 border-violet-500 p-6 rounded-r-lg">
                  <p className="text-violet-900">
                    <strong>Réglementation :</strong> Les entreprises de plus de 500 salariés doivent réaliser un Bilan d'Émissions de Gaz à Effet de Serre (BEGES) tous les 4 ans. Pour les PME, le bilan carbone devient un avantage concurrentiel majeur.
                  </p>
                </div>
              </section>

              {/* Méthodologie */}
              <section id="methodologie" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Calculator className="w-6 h-6 text-blue-600" />
                  2. Méthodologie du Bilan Carbone
                </h2>
                
                <p className="text-slate-600 mb-6">
                  Le bilan carbone est une méthode de comptabilisation des émissions de gaz à effet de serre (GES) développée par l'ADEME. Il permet de mesurer l'empreinte carbone d'une organisation.
                </p>

                <div className="space-y-4 mb-6">
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-green-600">1</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Définir le périmètre</h4>
                      <p className="text-slate-600 text-sm">Choisir les activités, sites et année de référence à analyser.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-green-600">2</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Collecter les données</h4>
                      <p className="text-slate-600 text-sm">Rassembler les données d'activité : consommations énergétiques, déplacements, achats, déchets...</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-green-600">3</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Calculer les émissions</h4>
                      <p className="text-slate-600 text-sm">Appliquer les facteurs d'émission de la Base Carbone ADEME à chaque donnée.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-green-600">4</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Analyser et planifier</h4>
                      <p className="text-slate-600 text-sm">Identifier les postes majeurs d'émissions et définir un plan de réduction.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Les 3 Scopes */}
              <section id="scopes" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <BarChart3 className="w-6 h-6 text-violet-600" />
                  3. Les 3 Scopes d'Émissions
                </h2>
                
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 border-l-4 border-red-500">
                    <div className="flex items-center gap-3 mb-3">
                      <Factory className="w-6 h-6 text-red-600" />
                      <h3 className="font-bold text-slate-900 text-lg">Scope 1 : Émissions directes</h3>
                    </div>
                    <p className="text-slate-600 mb-3">
                      Émissions produites directement par l'entreprise :
                    </p>
                    <ul className="text-slate-600 text-sm space-y-1">
                      <li>• Combustion de carburants (véhicules de société, chaudières)</li>
                      <li>• Procédés industriels</li>
                      <li>• Fuites de gaz réfrigérants</li>
                    </ul>
                  </div>
                  
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-6 border-l-4 border-amber-500">
                    <div className="flex items-center gap-3 mb-3">
                      <Zap className="w-6 h-6 text-amber-600" />
                      <h3 className="font-bold text-slate-900 text-lg">Scope 2 : Émissions indirectes énergétiques</h3>
                    </div>
                    <p className="text-slate-600 mb-3">
                      Émissions liées à l'énergie achetée :
                    </p>
                    <ul className="text-slate-600 text-sm space-y-1">
                      <li>• Électricité consommée</li>
                      <li>• Chauffage/climatisation (réseau de chaleur)</li>
                      <li>• Vapeur achetée</li>
                    </ul>
                  </div>
                  
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-l-4 border-green-500">
                    <div className="flex items-center gap-3 mb-3">
                      <Truck className="w-6 h-6 text-green-600" />
                      <h3 className="font-bold text-slate-900 text-lg">Scope 3 : Autres émissions indirectes</h3>
                    </div>
                    <p className="text-slate-600 mb-3">
                      Émissions de la chaîne de valeur (souvent les plus importantes) :
                    </p>
                    <ul className="text-slate-600 text-sm space-y-1">
                      <li>• Achats de biens et services</li>
                      <li>• Transport de marchandises et déplacements</li>
                      <li>• Déchets générés</li>
                      <li>• Utilisation des produits vendus</li>
                      <li>• Déplacements domicile-travail des salariés</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mt-6">
                  <p className="text-blue-900">
                    <strong>Important :</strong> Le Scope 3 représente souvent 70 à 90% des émissions totales d'une entreprise. C'est le plus complexe à mesurer mais aussi le plus impactant.
                  </p>
                </div>
              </section>

              {/* Bilan carbone dans les marchés publics */}
              <section id="marches-publics" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Target className="w-6 h-6 text-violet-600" />
                  4. Le Bilan Carbone dans les Marchés Publics
                </h2>
                
                <p className="text-slate-600 mb-6">
                  Les acheteurs publics intègrent de plus en plus le bilan carbone comme critère d'attribution. Voici comment il est généralement pris en compte :
                </p>

                <div className="space-y-4 mb-6">
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2">Critère d'attribution noté</h4>
                    <p className="text-slate-600 text-sm">
                      Le candidat doit fournir son bilan carbone ou une estimation des émissions liées à l'exécution du marché. Une note est attribuée en fonction de la performance carbone.
                    </p>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2">Condition d'exécution</h4>
                    <p className="text-slate-600 text-sm">
                      Le titulaire s'engage à respecter un plafond d'émissions ou à mettre en œuvre des actions de réduction pendant l'exécution du marché.
                    </p>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2">Spécification technique</h4>
                    <p className="text-slate-600 text-sm">
                      Exigence de produits à faible empreinte carbone, matériaux recyclés, transport décarboné, etc.
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl p-6">
                  <h4 className="font-bold text-slate-900 mb-3">Exemple de critère carbone dans un RC</h4>
                  <p className="text-slate-600 text-sm italic bg-white p-4 rounded-lg">
                    "Critère n°3 - Performance environnementale (20 points) : Le candidat présentera son bilan carbone (ou une estimation) ainsi que les mesures concrètes qu'il s'engage à mettre en œuvre pour réduire l'empreinte carbone du marché : optimisation logistique, véhicules propres, éco-conception, approvisionnement local..."
                  </p>
                </div>
              </section>

              {/* Rédiger son mémoire RSE */}
              <section id="memoire-rse" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <FileText className="w-6 h-6 text-green-600" />
                  5. Rédiger son Mémoire RSE/Carbone
                </h2>
                
                <p className="text-slate-600 mb-6">
                  Le mémoire RSE/Carbone est un document clé de votre réponse. Il doit démontrer votre engagement et vos actions concrètes.
                </p>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 mb-6">
                  <h4 className="font-bold text-slate-900 mb-4">Structure recommandée du mémoire RSE/Carbone</h4>
                  <ol className="space-y-3 text-slate-700">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                      <span><strong>Présentation de la démarche RSE</strong> : politique environnementale, gouvernance, certifications</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                      <span><strong>Bilan carbone de l'entreprise</strong> : résultats par scope, évolution, comparaison sectorielle</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                      <span><strong>Plan de réduction</strong> : objectifs chiffrés, trajectoire, actions programmées</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                      <span><strong>Engagements spécifiques au marché</strong> : transport, matériaux, déchets, fournisseurs</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">5</span>
                      <span><strong>Indicateurs de suivi</strong> : KPIs, reporting prévu, moyens de contrôle</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">6</span>
                      <span><strong>Preuves et attestations</strong> : labels, certifications, attestations fournisseurs</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                  <p className="text-amber-900">
                    <strong>Conseil :</strong> Soyez précis et chiffrez vos engagements. Un objectif de "-15% d'émissions sur 3 ans" est plus crédible que "nous nous engageons à réduire notre impact".
                  </p>
                </div>
              </section>

              {/* Outils */}
              <section id="outils" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Award className="w-6 h-6 text-amber-600" />
                  6. Outils et Ressources
                </h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Base Carbone ADEME</h4>
                    <p className="text-slate-600 text-sm mb-2">Base de données officielle des facteurs d'émission pour calculer votre bilan.</p>
                    <a href="https://bilans-ges.ademe.fr/" target="_blank" rel="noopener noreferrer" className="text-violet-600 text-sm hover:underline">
                      bilans-ges.ademe.fr →
                    </a>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Méthode Bilan Carbone®</h4>
                    <p className="text-slate-600 text-sm mb-2">Méthodologie officielle développée par l'Association Bilan Carbone (ABC).</p>
                    <a href="https://www.associationbilancarbone.fr/" target="_blank" rel="noopener noreferrer" className="text-violet-600 text-sm hover:underline">
                      associationbilancarbone.fr →
                    </a>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Diag Décarbon'Action</h4>
                    <p className="text-slate-600 text-sm mb-2">Accompagnement subventionné par BPI France pour les PME (4 000€ remboursés).</p>
                    <a href="https://www.bpifrance.fr/" target="_blank" rel="noopener noreferrer" className="text-violet-600 text-sm hover:underline">
                      bpifrance.fr →
                    </a>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-bold text-slate-900 mb-2">GHG Protocol</h4>
                    <p className="text-slate-600 text-sm mb-2">Standard international de comptabilisation des émissions de GES.</p>
                    <a href="https://ghgprotocol.org/" target="_blank" rel="noopener noreferrer" className="text-violet-600 text-sm hover:underline">
                      ghgprotocol.org →
                    </a>
                  </div>
                </div>
              </section>

              {/* Formulaire d'accompagnement */}
              <section id="formulaire-accompagnement" className="mb-12">
                <div className="bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600 rounded-2xl p-8 text-white">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                      <Recycle className="w-8 h-8" />
                    </div>
                    <h2 className="text-3xl font-bold mb-3">
                      Accompagnement Bilan Carbone & RSE
                    </h2>
                    <p className="text-green-100 max-w-2xl mx-auto">
                      DCE Express vous accompagne dans la réalisation de votre bilan carbone et la rédaction de vos mémoires RSE pour maximiser vos chances de remporter les marchés publics.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white/10 backdrop-blur rounded-xl p-5 text-center">
                      <Calculator className="w-8 h-8 mx-auto mb-3" />
                      <h4 className="font-bold mb-2">Bilan Carbone</h4>
                      <p className="text-green-100 text-sm">Réalisation complète de votre bilan carbone selon la méthode ADEME</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-xl p-5 text-center">
                      <FileText className="w-8 h-8 mx-auto mb-3" />
                      <h4 className="font-bold mb-2">Mémoire RSE</h4>
                      <p className="text-green-100 text-sm">Rédaction de votre mémoire RSE/Carbone adapté à chaque marché</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-xl p-5 text-center">
                      <TrendingDown className="w-8 h-8 mx-auto mb-3" />
                      <h4 className="font-bold mb-2">Plan de Réduction</h4>
                      <p className="text-green-100 text-sm">Définition d'objectifs et d'actions concrètes de décarbonation</p>
                    </div>
                  </div>

                  {submitted ? (
                    <div className="bg-white rounded-xl p-8 text-center">
                      <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">Demande envoyée !</h3>
                      <p className="text-slate-600">
                        Notre équipe vous recontacte sous 24h pour discuter de votre projet.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 md:p-8">
                      <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">
                        Demander un devis gratuit
                      </h3>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label htmlFor="company" className="text-slate-700">Entreprise *</Label>
                          <Input 
                            id="company"
                            placeholder="Nom de votre entreprise"
                            value={formData.company}
                            onChange={(e) => setFormData({...formData, company: e.target.value})}
                            required
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-slate-700">Email *</Label>
                          <Input 
                            id="email"
                            type="email"
                            placeholder="votre@email.com"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            required
                            className="mt-1"
                          />
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label htmlFor="phone" className="text-slate-700">Téléphone</Label>
                          <Input 
                            id="phone"
                            placeholder="06 12 34 56 78"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="sector" className="text-slate-700">Secteur d'activité *</Label>
                          <Input 
                            id="sector"
                            placeholder="BTP, Services, Industrie..."
                            value={formData.sector}
                            onChange={(e) => setFormData({...formData, sector: e.target.value})}
                            required
                            className="mt-1"
                          />
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <Label htmlFor="employees" className="text-slate-700">Nombre de salariés</Label>
                        <Input 
                          id="employees"
                          placeholder="Ex: 25"
                          value={formData.employees}
                          onChange={(e) => setFormData({...formData, employees: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="mb-6">
                        <Label htmlFor="needs" className="text-slate-700">Décrivez votre besoin *</Label>
                        <Textarea 
                          id="needs"
                          placeholder="Ex: Nous souhaitons réaliser notre premier bilan carbone et être accompagnés pour répondre à un appel d'offres qui demande un mémoire RSE..."
                          value={formData.needs}
                          onChange={(e) => setFormData({...formData, needs: e.target.value})}
                          required
                          rows={4}
                          className="mt-1"
                        />
                      </div>
                      
                      <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-lg"
                      >
                        {isSubmitting ? (
                          <>Envoi en cours...</>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Envoyer ma demande
                          </>
                        )}
                      </Button>
                      
                      <p className="text-slate-500 text-sm text-center mt-4">
                        Réponse sous 24h • Devis gratuit et sans engagement
                      </p>
                    </form>
                  )}
                </div>
              </section>

              {/* CTA Final */}
              <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-8 text-center text-white">
                <h3 className="text-2xl font-bold mb-4">
                  Trouvez des marchés avec critères environnementaux
                </h3>
                <p className="text-violet-100 mb-6 max-w-xl mx-auto">
                  DCE Express identifie automatiquement les appels d'offres incluant des critères carbone et RSE adaptés à votre secteur.
                </p>
                <Link to="/register">
                  <Button className="bg-white text-violet-600 hover:bg-slate-100 px-8 py-3 text-lg">
                    Essayer gratuitement
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-12">
          <div className="container mx-auto px-4 text-center">
            <p className="text-slate-400">
              © 2026 DCE Express - Tous droits réservés
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
